import { BUILD_ENVIRONMENTS } from '@src/config/constants'
import { IS_PROD_BUILD } from '@src/config/env'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b'
import { createBatches } from '@src/utils/general.utils'

import Product, { IProduct } from '../../domains/Product/Product'
import QuinceApiError from '../../domains/quince-api-error'
import filterFalsy from '../../utils/filterFalsy'
import { sandServicesHttpClient } from '../../utils/HttpClient'
import { B2B_URL_PREFIX } from '../cartCheckoutService/constants'

import {
  GetProductRequest,
  GetProductRequestWithSpids,
  GetProductRequestWithContentfulProductIds,
  GetProductRequestWithInternalProductIds,
} from './productService.types'

/**
 * API Configuration Rules:
 * - useLite=false: Executes full enrichment based on provided options
 * - useLite=true: Executes lite enrichment, ignoring provided options
 * - options=ENRICH_REFRESH_LITE: Forces lite enrichment regardless of useLite flag. Other options passed will also be enriched if useLite is false.
 * - options=ENRICH_ONLY_TAGS: Restricts enrichment to tags only. Value of useLite doesnt matter. Other Options passed are ignored.
 */
export async function fetchAndEnrichProducts(fetchProductRequest?: GetProductRequest): Promise<Product[]> {
  const {
    spids = undefined,
    internalProductIds = undefined,
    contentfulProductIds,
    options = undefined,
    useLite = false,
    zipCode = undefined,
    extraParams = undefined,
  } = fetchProductRequest ?? {}
  let productList: IProduct[] = []
  if ((spids?.length ?? 0) == 0 && (contentfulProductIds?.length ?? 0) == 0 && (internalProductIds?.length ?? 0) == 0) {
    return []
  }

  try {
    const apiUrlToAppend = getStoreProgramIdFromStorage() ? `/${B2B_URL_PREFIX}` : ''
    const { data: products } = await sandServicesHttpClient.get<IProduct[]>(`${apiUrlToAppend}/products/enrich`, {
      params: {
        ...extraParams,
        productIds: spids?.join(','),
        internalProductIds: internalProductIds?.join(','),
        contentfulProductIds: contentfulProductIds?.join(','),
        options: options?.join(','),
        zipCode: zipCode,
        environment: IS_PROD_BUILD ? BUILD_ENVIRONMENTS.PROD.toUpperCase() : BUILD_ENVIRONMENTS.STAGE.toUpperCase(),
        useLite,
      },
    })
    productList = products
  } catch (e) {
    throw new QuinceApiError({
      error: e as Error,
      message: 'Failed while fetching products enrich getProducts() runtime',
      apiName: 'PRODUCTS_ENRICH_FETCH',
      data: {
        fetchProductRequest,
      },
    })
  }

  return filterFalsy(productList).map(x => new Product(x))
}

export async function fetchAndEnrichProductsBySpidsUsingBatching(
  fetchProductRequest: GetProductRequestWithSpids,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.spids)
  const products = await Promise.all(
    batches.map(batch => fetchAndEnrichProducts({ ...fetchProductRequest, spids: batch })),
  )
  return products.flat()
}

export async function fetchAndEnrichProductsByContentfulIdsUsingBatching(
  fetchProductRequest: GetProductRequestWithContentfulProductIds,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.contentfulProductIds)
  const products = await Promise.all(
    batches.map(batch => fetchAndEnrichProducts({ ...fetchProductRequest, contentfulProductIds: batch })),
  )
  return products.flat()
}

export async function fetchAndEnrichProductsByInternalProductIdsUsingBatching(
  fetchProductRequest: GetProductRequestWithInternalProductIds,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.internalProductIds)
  const products = await Promise.all(
    batches.map(batch => fetchAndEnrichProducts({ ...fetchProductRequest, internalProductIds: batch })),
  )
  return products.flat()
}
